import { post, read } from '@/utils/request';
import type { CreditAPI } from './typings';
import type { ListData } from '@bluevision/tools';
import type { CommonModule } from '../common/typings';
import type { WalletRechargeQuota } from '@/pages/Wallet/interface';
import type { WalletAPI } from '../wallet/typings';
import i18n from '@/i18n';
import type { OrderRecords } from '../wallet/interface';

// 获取信用卡交易明细
const getCardAmountList = (params: CreditAPI.Request.CardAmountParams) =>
  read<ListData<CreditAPI.Response.CardAmountInfo>>(
    'credit-card-amount-changes',
    params,
    undefined,
    undefined,
    undefined,
    {
      baseAPI: '/api/v1',
    },
  );

// 获取信用卡第三方交易记录
const getCardOrderList = (
  params: OrderRecords.Request.OrderRecordsInfo,
  signal?: AbortController['signal'],
) => {
  const recharge_way = params?.recharge_way;
  const order_status = params?.order_status;

  const param = {
    ...params,
    recharge_way: Array.isArray(recharge_way) ? recharge_way.join(',') : recharge_way,
    order_status: Array.isArray(order_status) ? order_status.join(',') : order_status,
    start_date: params?.submit_time?.[0],
    end_date: params?.submit_time?.[1],
    current_page: Number(params?.currentPage),
    page_size: Number(params?.pageSize),
  };

  delete param?.currentPage;
  delete param?.pageSize;
  delete param?.submit_time;

  return post<OrderRecords.Response.OrderRecordsList>(
    'credit-card-third-recharge-record',
    param,
    undefined,
    undefined,
    { signal, isCamel: false, isLower: false },
    { baseAPI: '/api/v1' },
  );
};

// 获取信用卡额度变更记
const getSpendCapChanges = (params: CreditAPI.Request.SpendCapParams) =>
  read<ListData<CreditAPI.Response.SpendCapInfo>>(
    'credit-card-spend-cap-changes',
    {
      ...params,
      startTime: params?.submitTime?.[0],
      endTime: params?.submitTime?.[1],
      submitTime: undefined,
    },
    undefined,
    undefined,
    undefined,
    {
      baseAPI: '/api/v1',
    },
  );

// 获取信用卡灰度
const getCreditGrayScale = () =>
  read<CommonModule.Common.Feature>(
    'credit-card-grayscale',
    undefined,
    undefined,
    undefined,
    undefined,
    { baseAPI: '/api/v1' },
  );

// 获取皮卡授权用户信息
const getCreditPayoneerUserInfo = () =>
  read<WalletRechargeQuota.Response.PayoneerUserInfo>(
    'credit-card-payoneer-user-info',
    undefined,
    undefined,
    undefined,
    { isCamel: false },
    { baseAPI: '/api/v1' },
  );

// 获取皮卡授权链接
const getCreditPayoneerOauthUrl = () =>
  read(
    'credit-card-payoneer-oauth-url',
    undefined,
    undefined,
    undefined,
    { isCamel: false },
    { baseAPI: '/api/v1' },
  );

const mock = '';
// 获取订单详情，皮卡充值下单
const getCreditPayoneerRechargeDebitInfo = (params: WalletAPI.PayoneerRechargeDebitParams) =>
  post<WalletAPI.PayoneerRechargeDebitInfo>(
    'credit-card-payoneer-recharge-debit',
    params,
    i18n.t('failed_to_obtain_order_details'),
    undefined,
    { isCamel: false, isLower: false },
    { baseAPI: '/api/v1' },
  );
// 皮卡充值下单确认
const creditPayoneerConfirm = (data: { order_num: string }) => {
  return post<WalletRechargeQuota.Response.PayoneerConfirm>(
    'credit-card-payoneer-recharge-commit',
    data,
    i18n.t('order_submission_failed'),
    undefined,
    { isCamel: false, isLower: false },
    { baseAPI: '/api/v1' },
  );
};

// 获取订单详情，Airwallex充值下单
const getCreditAirwallexRechargeDebitInfo = (params: WalletAPI.PayoneerRechargeDebitParams) =>
  post<WalletAPI.PayoneerRechargeDebitInfo>(
    `${mock}credit-card-airwallex-recharge-debit`,
    params,
    i18n.t('failed_to_obtain_order_details'),
    undefined,
    { isCamel: false, isLower: false },
    { baseAPI: '/api/v1' },
  );

// Airwallex充值下单确认
const CreditAirwallexConfirm = (data: { order_num: string }) => {
  return post<WalletRechargeQuota.Response.AirwallexConfirm>(
    `${mock}credit-card-airwallex-recharge-commit`,
    data,
    i18n.t('order_submission_failed'),
    undefined,
    { isCamel: false, isLower: false },
    { baseAPI: '/api/v1' },
  );
};

// 获取订单详情，WorldFirst充值下单
const getCreditWorldFirstRechargeDebitInfo = (params: WalletAPI.WorldFirstRechargeDebitParams) =>
  post<WalletAPI.WorldFirstRechargeDebitInfo>(
    'credit-card-worldfirst-recharge-debit',
    params,
    i18n.t('failed_to_obtain_order_details'),
    undefined,
    { isCamel: false, isLower: false },
    { baseAPI: '/api/v1' },
  );

// 获取订单详情，PingPong充值下单
const getCreditPingPongRechargeDebitInfo = (params: WalletAPI.WorldFirstRechargeDebitParams) =>
  post<WalletAPI.WorldFirstRechargeDebitInfo>(
    'credit-card-pingpong-recharge-debit',
    params,
    i18n.t('failed_to_obtain_order_details'),
    undefined,
    { isCamel: false, isLower: false },
    { baseAPI: '/api/v1' },
  );

// WorldFirst充值下单确认
const creditWorldFirstConfirm = (data: { order_num: string; redirect_url?: string }) => {
  const defaultRedirectUrl = `${window.location.origin}/credit/transactions/order?cb_from=WORLDFIRST`;
  return post<WalletRechargeQuota.Response.WorldFirstConfirm>(
    'credit-card-worldfirst-recharge-commit',
    { ...data, redirect_url: data.redirect_url || defaultRedirectUrl },
    i18n.t('order_submission_failed'),
    undefined,
    { isCamel: false, isLower: false },
    { baseAPI: '/api/v1' },
  );
};

// PingPong充值下单确认
const creditPingPongConfirm = (data: { order_num: string }) => {
  // const defaultRedirectUrl = `${window.location.origin}/credit/transactions/order?cb_from=WORLDFIRST`;
  return post<WalletRechargeQuota.Response.WorldFirstConfirm>(
    'credit-card-pingpong-recharge-commit',
    data,
    i18n.t('order_submission_failed'),
    undefined,
    { isCamel: false, isLower: false },
    { baseAPI: '/api/v1' },
  );
};

export {
  getCardAmountList,
  getSpendCapChanges,
  getCreditGrayScale,
  getCreditPayoneerUserInfo,
  getCreditPayoneerOauthUrl,
  getCreditPayoneerRechargeDebitInfo,
  creditPayoneerConfirm,
  getCreditAirwallexRechargeDebitInfo,
  CreditAirwallexConfirm,
  getCreditWorldFirstRechargeDebitInfo,
  creditWorldFirstConfirm,
  getCreditPingPongRechargeDebitInfo,
  creditPingPongConfirm,
  getCardOrderList,
};
